import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/refer", label: "Support Coordinators" },
  { to: "/resources", label: "Resources" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function PublicNav() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const dashHref =
    user && user.role === "admin" ? "/admin"
      : user && user.role === "employee" ? "/academy"
      : user && user.role === "family" ? "/family"
      : null;

  return (
    <header className="wc-glass-nav" data-testid="public-nav">
      <div className="wc-container flex items-center justify-between py-3 gap-6">
        <Link to="/" className="flex items-center shrink-0" data-testid="brand-logo">
          <BrandLogo className="h-16 md:h-20" />
        </Link>

        <nav className="hidden xl:flex items-center gap-5">
          {links.map((l) => (
            <NavLink
              key={l.to + l.label}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors whitespace-nowrap ${isActive ? "text-brand-navy" : "text-slate-500 hover:text-brand-navy"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <NavLink
            to={dashHref || "/employee-portal"}
            data-testid="nav-employee-portal"
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${isActive ? "text-brand-navy" : "text-brand-navy/85 hover:text-brand-navy"}`
            }
          >
            <Lock className="w-3 h-3 opacity-70" strokeWidth={2} />
            {dashHref ? "Dashboard" : "Employee Portal"}
          </NavLink>
        </nav>

        <div className="hidden xl:flex items-center gap-4">
          <button onClick={() => nav("/contact")} className="wc-btn-orange !py-2.5 !px-5 text-xs uppercase tracking-widest font-bold" data-testid="nav-request-services-btn">
            Request Services <ArrowRight className="w-3.5 h-3.5"/>
          </button>
        </div>

        <button className="xl:hidden p-2 text-brand-navy" data-testid="nav-mobile-toggle" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-brand-stone/60 bg-brand-cream/95 backdrop-blur-xl">
          <div className="wc-container py-4 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink key={l.to + l.label} to={l.to} onClick={() => setOpen(false)}
                data-testid={`mnav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="py-2 text-slate-700 hover:text-brand-navy font-medium">
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to={dashHref || "/employee-portal"}
              onClick={() => setOpen(false)}
              data-testid="mnav-employee-portal"
              className="inline-flex items-center gap-1.5 py-2 text-slate-700 hover:text-brand-navy font-medium"
            >
              <Lock className="w-3.5 h-3.5 opacity-70" strokeWidth={2} />
              {dashHref ? "Dashboard" : "Employee Portal"}
            </NavLink>
            <button onClick={() => { setOpen(false); nav("/contact"); }} className="wc-btn-orange mt-2 w-full" data-testid="mnav-request-services-btn">
              Request Services
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
